<template>
    <div id="access-control">
        <a-tabs v-model="activeTab" class="access-control-tabs">
            <!-- Tab 1: IP Whitelist -->
            <a-tab-pane key="whitelist" :tab="$t('settings.ipWhitelist')">
                <supsis-table :columns="cols()" :rows="getFilteredRows()">
                    <template #header>
                        <div class="d-flex justify-content-between align-items-center">
                            <a-input-search
                                v-model="searchText"
                                :placeholder="$t('message.search')"
                                class="search-input"
                                @search="onSearch"
                            />
                            <div class="page-header">
                                <div class="title">{{ $t("settings.accessControl") }}</div>
                                <div class="subtitle">{{ $t("settings.accessControlSubTitles") }}</div>
                            </div>
                            <SupsisButton ghost @click="showModalWithoutEdit">
                                {{ $t('settings.whiteListAdd') }}
                                <md-icon>add</md-icon>
                            </SupsisButton>
                        </div>
                    </template>
                </supsis-table>
            </a-tab-pane>

            <!-- Tab 2: Sessions -->
            <a-tab-pane key="sessions" :tab="$t('sessions.sessionManagement')">
                <div class="sessions-tab">
                    <div class="sessions-header d-flex justify-content-between align-items-center mb-3">
                        <div class="page-header">
                            <div class="title">{{ $t("sessions.sessionManagement") }}</div>
                            <div class="subtitle">{{ $t("sessions.allSessionsDesc") }}</div>
                        </div>
                        <div class="sessions-filters d-flex align-items-center">
                            <a-select
                                v-model="selectedUserId"
                                :placeholder="$t('sessions.filterByUser')"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                style="width: 250px; margin-right: 12px;"
                                @change="onUserFilterChange"
                            >
                                <a-select-option v-for="user in usersList" :key="user._id" :value="user._id">
                                    {{ user.name || user.email }}
                                </a-select-option>
                            </a-select>
                            <a-button type="default" icon="reload" @click="fetchAllSessions" :loading="sessionsLoading">
                                {{ $t('message.refresh') }}
                            </a-button>
                        </div>
                    </div>

                    <div v-if="sessionsLoading" class="sessions-loading">
                        <a-spin size="large" />
                    </div>

                    <div v-else-if="allSessions.length === 0" class="sessions-empty">
                        <a-empty :description="$t('sessions.noActiveSessions')" />
                    </div>

                    <div v-else class="sessions-list">
                        <SessionCard
                            v-for="session in allSessions"
                            :key="session._id"
                            :session="session"
                            :showUserInfo="true"
                            @revoke="handleAdminRevoke"
                        />
                    </div>

                    <div v-if="sessionsPagination.totalPages > 1" class="sessions-pagination mt-3">
                        <a-pagination
                            :current="sessionsPagination.page"
                            :total="sessionsPagination.total"
                            :pageSize="sessionsPagination.perPage"
                            @change="onSessionPageChange"
                        />
                    </div>
                </div>
            </a-tab-pane>
        </a-tabs>

        <Modal2
            v-if="isModalShow"
            id="white-list-form"
            :title="$t('settings.whiteList')"
            @cancel="hideModal"
            :show-footer="false"
        >
            <div class="form-group">
                <label for="ipAddress">{{ $t("message.ipAddress") }}</label>
                <div class="ip-address">
                    <a-input v-model="form.ipAddress" id="ipAddress" placeholder="0.0.0.0" />
                    <SupsisButton @click="takeIpAddress" primary class="detect-ip-btn">
                        {{ $t('settings.detectIpAddress') }}
                    </SupsisButton>
                </div>
            </div>
            <div class="form-group">
                <label for="description">{{ $t('tables.description') }}</label>
                <a-input v-model="form.description" id="description" />
            </div>
            <div class="d-flex justify-content-end">
                <SupsisButton @click="handleSave" primary> {{ $t('message.save') }} </SupsisButton>
            </div>
        </Modal2>
    </div>
</template>

<script>
import { log2 } from "../../../../../utils/log2";
import ActionEdit from "../../../../../components/svg/ActionEdit.vue";
import ActionDelete from "../../../../../components/svg/ActionDelete.vue";
import Pagination from "../../../../../components/Pagination.vue";
import MultiContent from "../../../../../components/MultiContent.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../components/SupsisButton.vue";
import CreateButton from "../../../../../components/CreateButton.vue";
import SessionCard from "../../../../../components/session/SessionCard.vue";
import { WHITE_LIST_API } from "../../../../../api/whitelist";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { mapState, mapActions, mapGetters } from "vuex";

const log = log2("AccessControl");

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);
export default {
    name: "AccessControl",
    components: {
        CreateButton,
        SupsisButton,
        SupsisTable,
        MultiContent,
        ActionEdit,
        ActionDelete,
        Pagination,
        Modal2,
        SessionCard,
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            allSessions: (state) => state.session.allSessions,
            sessionsLoading: (state) => state.session.loading,
            sessionsPagination: (state) => state.session.pagination,
            usersList: (state) => state.user.users.data,
        }),
    },
    data() {
        return {
            activeTab: "whitelist",
            loading: false,
            editMode: false,
            searchText: undefined,
            selectedUserId: undefined,
            cols: () => [
                {
                    field: "ipAddress",
                    key: "ipAddress",
                    title: this.$t("message.ipAddress"),
                    renderBodyCell: ({ row }) => {
                        return row.ipAddress;
                    },
                },
                {
                    field: "description",
                    key: "description",
                    title: this.$t("message.description"),
                    renderBodyCell: ({ row }) => {
                        return row.description;
                    },
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="d-flex w-100 justify-content-end">
                                <a-button
                                    className="actionButton d-flex align-items-center"
                                    on-click={() => this.remove(row._id)}
                                >
                                    <ActionDelete />
                                </a-button>
                            </div>
                        );
                    },
                },
            ],
            isModalShow: false,
            form: {
                ipAddress: "",
                description: "",
            },
            data: [],
        };
    },
    methods: {
        ...mapActions({
            fetchAllSessionsAction: "session/fetchAllSessions",
            adminRevokeSessionAction: "session/adminRevokeSession",
            fetchUsersAction: "user/getUsers",
        }),
        takeIpAddress() {
            this.form.ipAddress = this.user.lastLoginIPAddress;
        },
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
        },
        showModalWithoutEdit() {
            this.editMode = false;
            this.showModal();
        },
        getFilteredRows() {
            const items = this.searchText
                ? this.data.filter(
                      (x) =>
                          x.ipAddress.toLowerCase().includes(this.searchText.toLowerCase()) ||
                          x.description.toLowerCase().includes(this.searchText.toLowerCase())
                  )
                : this.data;
            return items;
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },
        get() {
            this.isBusy = true;
            WHITE_LIST_API.getWhiteList()
                .then((list) => {
                    this.data = list;
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("settings.deleteWhiteListTitle"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    WHITE_LIST_API.removeWhiteList(id)
                        .then((x) => {
                            this.data = this.data.filter((y) => y._id !== id);
                            this.$toasted.show(this.$t("settings.whiteListDeletedSuccessfully"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        })
                        .catch((err) => {
                            this.$toasted.error(this.$t("settings.whiteListDeletedFailed"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        });
                });
        },
        handleSave() {
            if (this.editMode) {
            } else {
                WHITE_LIST_API.addWhiteList(this.form)
                    .then((data) => {
                        this.$toasted.show(this.$t("settings.whiteListAddedSuccessfully"), {
                            duration: 3000,
                            position: "bottom-center",
                        });
                        this.data = [...this.data, data];
                        this.form = {
                            ipAddress: "",
                            description: "",
                        };
                        this.hideModal();
                    })
                    .catch((err) => {
                        console.log(err);
                        this.$toasted.error(this.$t("settings.whiteListAddedFailed"), {
                            duration: 3000,
                            position: "bottom-center",
                        });
                    });
            }
        },
        fetchAllSessions() {
            var params = {
                page: this.sessionsPagination.page,
                perPage: this.sessionsPagination.perPage,
            };
            if (this.selectedUserId) {
                params.userId = this.selectedUserId;
            }
            this.fetchAllSessionsAction(params);
        },
        onSessionPageChange(page) {
            var params = {
                page,
                perPage: this.sessionsPagination.perPage,
            };
            if (this.selectedUserId) {
                params.userId = this.selectedUserId;
            }
            this.fetchAllSessionsAction(params);
        },
        onUserFilterChange() {
            this.fetchAllSessionsAction({
                page: 1,
                perPage: this.sessionsPagination.perPage,
                userId: this.selectedUserId || undefined,
            });
        },
        handleAdminRevoke(sessionId) {
            this.$dialog
                .confirm(this.$t("sessions.revokeConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.adminRevokeSessionAction({ sessionId, reason: "admin_revoked" })
                        .then(() => {
                            this.$toasted.show(this.$t("sessions.revokeSuccess"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        })
                        .catch((err) => {
                            this.$toasted.error(this.$t("sessions.revokeFailed"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        });
                });
        },
    },
    watch: {
        activeTab(newTab) {
            if (newTab === "sessions") {
                if (this.$route.path !== "/settings/sessions") {
                    this.$router.push("/settings/sessions");
                }
                this.fetchAllSessions();
                if (!this.usersList || this.usersList.length === 0) {
                    this.fetchUsersAction({ query: {} });
                }
            } else if (newTab === "whitelist") {
                if (this.$route.path !== "/settings/access-control") {
                    this.$router.push("/settings/access-control");
                }
            }
        },
        "$route.path": {
            immediate: true,
            handler(path) {
                if (path === "/settings/sessions") {
                    this.activeTab = "sessions";
                } else if (path === "/settings/access-control") {
                    this.activeTab = "whitelist";
                }
            },
        },
    },
    mounted() {
        this.get();
        if (this.$route.path === "/settings/sessions") {
            this.activeTab = "sessions";
        }
    },
};
</script>

<style lang="scss">
#white-list-form {
    .ip-address {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        .supsis-button {
            white-space: nowrap;
            min-width: fit-content;
        }
    }
}
#access-control {
    .access-control-tabs {
        .ant-tabs-nav {
            margin-bottom: 16px;
        }
    }
    .search-input {
        width: 200px;
    }
    .ip-address {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        .supsis-button {
            white-space: nowrap;
        }
    }
    .form-group {
        margin-bottom: 20px;
    }
    .title {
        font-size: 16px; font-weight: bold; color: #333;
        margin-right: 8px;
    }
    .sessions-tab {
        padding: 16px 0;
    }
    .sessions-loading,
    .sessions-empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 200px;
    }
    .sessions-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .sessions-pagination {
        display: flex;
        justify-content: center;
    }
}
</style>
